import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';

const skillSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  type: z.enum(['technical', 'soft', 'language', 'certification', 'tool', 'framework', 'product']),
  description: z.string().optional(),
  is_bucket: z.boolean(),
  parent_id: z.string().nullable(),
});

type SkillFormData = z.infer<typeof skillSchema>;

interface Skill {
  id: string;
  name: string;
  type: string | null;
  parent_id: string | null;
  is_bucket: boolean;
  position: number;
  description: string | null;
}

interface SkillFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: Skill | null;
  parentId: string | null;
  buckets: Skill[];
  onSubmit: (data: SkillFormData, id?: string) => void;
}

const SkillFormDialog = ({ open, onOpenChange, skill, parentId, buckets, onSubmit }: SkillFormDialogProps) => {
  const form = useForm<SkillFormData>({
    resolver: zodResolver(skillSchema),
    defaultValues: {
      name: '',
      type: 'technical',
      description: '',
      is_bucket: false,
      parent_id: null,
    },
  });

  useEffect(() => {
    if (skill) {
      form.reset({
        name: skill.name,
        type: (skill.type as any) || 'technical',
        description: skill.description || '',
        is_bucket: skill.is_bucket,
        parent_id: skill.parent_id,
      });
    } else {
      form.reset({
        name: '',
        type: 'technical',
        description: '',
        is_bucket: false,
        parent_id: parentId,
      });
    }
  }, [skill, parentId, form]);

  const handleSubmit = (data: SkillFormData) => {
    onSubmit(data, skill?.id);
    onOpenChange(false);
  };

  const isBucket = form.watch('is_bucket');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{skill ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
          <DialogDescription>
            {skill ? 'Update the skill details below.' : 'Add a new skill or bucket to the taxonomy.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="is_bucket"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Create as Bucket</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Buckets are categories that can contain other skills
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isBucket ? 'Bucket Name' : 'Skill Name'} *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={isBucket ? 'e.g., Frontend Development' : 'e.g., React'} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isBucket && (
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="framework">Framework</SelectItem>
                        <SelectItem value="tool">Tool</SelectItem>
                        <SelectItem value="soft">Soft Skill</SelectItem>
                        <SelectItem value="language">Language</SelectItem>
                        <SelectItem value="certification">Certification</SelectItem>
                        <SelectItem value="product">Product</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="parent_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parent Bucket</FormLabel>
                  <Select 
                    onValueChange={(v) => field.onChange(v === 'none' ? null : v)} 
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="No parent (root level)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No parent (root level)</SelectItem>
                      {buckets
                        .filter(b => b.id !== skill?.id)
                        .map(bucket => (
                          <SelectItem key={bucket.id} value={bucket.id}>
                            {bucket.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Optional description..." rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">{skill ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SkillFormDialog;